<div class="sabu-modal">
  <div class="sabu-modal__header">
    <div class="sabu-modal__title-group">
      <h4 class="sabu-modal__title">Bulk Upload RDMS Finish Products</h4>
      <p class="sabu-modal__subtitle">{{ subtitle }}</p>
    </div>
    <button type="button" class="sabu-modal__close-btn" (click)="activeModal.dismiss('Cross click')">
      <i data-lucide="x"></i>
    </button>
  </div>

  <div class="sabu-modal__body" *ngIf="step === 'upload'">
    <div class="sabu-upload-panel" *ngIf="!loadingLookups; else loadingTpl">
      <div class="sabu-upload-hero">
        <div class="sabu-upload-hero__icon">
          <i data-lucide="file-spreadsheet"></i>
        </div>
        <div>
          <h5 class="sabu-upload-hero__title">Upload finish product file</h5>
          <p class="sabu-upload-hero__text">
            Put values in any readable form — ID or name, free text, or full RDMS export. The importer reads the file and fills what it can; unmatched text is still saved.
          </p>
        </div>
      </div>

      <div class="sabu-actions-row">
        <button type="button" class="btn btn--outline" (click)="downloadTemplate('csv')">
          <i data-lucide="download"></i>
          Download CSV Template
        </button>
        <button type="button" class="btn btn--outline" (click)="downloadTemplate('xlsx')">
          <i data-lucide="download"></i>
          Download Excel Template
        </button>
        <label class="btn btn--primary sabu-file-label">
          <i data-lucide="upload"></i>
          Select CSV / Excel
          <input
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            class="d-none"
            (change)="onFileSelected($event)"
            [disabled]="parsing" />
        </label>
      </div>

      <p class="sabu-hint" *ngIf="fileName">Selected: {{ fileName }}</p>
      <p class="sabu-error" *ngIf="fileError">{{ fileError }}</p>
      <p class="sabu-hint" *ngIf="parsing">Parsing file…</p>
      <p class="sabu-hint" *ngIf="enrichingIngredients">{{ enrichProgress || 'Matching ingredients to master…' }}</p>

      <div class="sabu-help">
        <div class="sabu-help__item">
          <strong>File columns</strong>
          <span>All 15 Configure sections are supported. Master fields accept ID and/or name. Full RDMS export (.xls) files work as-is.</span>
        </div>
        <div class="sabu-help__item">
          <strong>Configure per row</strong>
          <span>Open Configure to review mapped fields and fill anything still empty. All fields are optional.</span>
        </div>
        <div class="sabu-help__item">
          <strong>Status &amp; images</strong>
          <span>Default status is Draft. Maximum {{ maxRows }} rows per upload (same as recipe / supplement). Front image can be uploaded in the preview table.</span>
        </div>
      </div>
    </div>
    <ng-template #loadingTpl>
      <p class="sabu-hint">Loading master data for dropdowns…</p>
    </ng-template>
  </div>

  <div class="sabu-modal__body" *ngIf="step === 'preview'">
    <div class="sabu-toolbar">
      <div class="sabu-summary">
        <span class="sabu-badge sabu-badge--total">Total: {{ totalCount }}</span>
        <span class="sabu-badge sabu-badge--valid">Valid: {{ validCount }}</span>
        <span class="sabu-badge sabu-badge--error">Errors: {{ errorCount }}</span>
      </div>
      <div class="sabu-global-status">
        <label>Default status for all rows</label>
        <ng-select class="sabu-status-select" [items]="statusOptions" bindLabel="label" bindValue="value"
          [(ngModel)]="globalStatus" (ngModelChange)="onGlobalStatusChange()"
          [clearable]="false" appendTo="body"></ng-select>
      </div>
    </div>

    <p class="sabu-hint" *ngIf="enrichingIngredients" style="margin-bottom: 10px;">
      {{ enrichProgress || 'Loading FAO Food Groups and allergens from ingredient master…' }}
    </p>

    <div class="sabu-table-container">
      <table class="sabu-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Image</th>
            <th>Certificate</th>
            <th>License</th>
            <th>Brand *</th>
            <th>Product *</th>
            <th>Status</th>
            <th>Configured</th>
            <th>Validation</th>
            <th *ngIf="errorCount > 0">Issues</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of parseResult?.rows; let i = index" [class.sabu-row--error]="!row.valid">
            <td class="sabu-num-cell">{{ row.rowNumber }}</td>
            <td class="sabu-image-cell">
              <div class="sabu-thumb" *ngIf="row.selections.frontImage; else noThumb">
                <img [src]="getRowImageUrl(row)" alt="Product image" />
                <button type="button" class="sabu-thumb__remove" title="Remove image"
                  (click)="removeRowImage(row)" [disabled]="row.selections.frontImageUploading">
                  <i class="ft-x"></i>
                </button>
              </div>
              <ng-template #noThumb>
                <label class="sabu-thumb-upload" [class.sabu-thumb-upload--busy]="row.selections.frontImageUploading">
                  <i class="ft-image" *ngIf="!row.selections.frontImageUploading"></i>
                  <span *ngIf="row.selections.frontImageUploading">…</span>
                  <span *ngIf="!row.selections.frontImageUploading">Add</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" class="d-none"
                    (change)="onRowImageSelected($event, row)"
                    [disabled]="row.selections.frontImageUploading" />
                </label>
              </ng-template>
            </td>
            <td class="sabu-image-cell">
              <div class="sabu-thumb" *ngIf="row.selections.authorizedCertificateImage; else noCert">
                <img [src]="getRowCertificateUrl(row)" alt="Certificate" />
                <button type="button" class="sabu-thumb__remove" title="Remove certificate"
                  (click)="removeRowCertificate(row)" [disabled]="row.selections.authorizedCertificateUploading">
                  <i class="ft-x"></i>
                </button>
              </div>
              <ng-template #noCert>
                <label class="sabu-thumb-upload" [class.sabu-thumb-upload--busy]="row.selections.authorizedCertificateUploading">
                  <i class="ft-file" *ngIf="!row.selections.authorizedCertificateUploading"></i>
                  <span *ngIf="row.selections.authorizedCertificateUploading">…</span>
                  <span *ngIf="!row.selections.authorizedCertificateUploading">Add</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" class="d-none"
                    (change)="onRowCertificateSelected($event, row)"
                    [disabled]="row.selections.authorizedCertificateUploading" />
                </label>
              </ng-template>
            </td>
            <td class="sabu-image-cell">
              <div class="sabu-thumb" *ngIf="row.selections.authorizedLicenseImage; else noLic">
                <img [src]="getRowLicenseUrl(row)" alt="License" />
                <button type="button" class="sabu-thumb__remove" title="Remove license"
                  (click)="removeRowLicense(row)" [disabled]="row.selections.authorizedLicenseUploading">
                  <i class="ft-x"></i>
                </button>
              </div>
              <ng-template #noLic>
                <label class="sabu-thumb-upload" [class.sabu-thumb-upload--busy]="row.selections.authorizedLicenseUploading">
                  <i class="ft-file-text" *ngIf="!row.selections.authorizedLicenseUploading"></i>
                  <span *ngIf="row.selections.authorizedLicenseUploading">…</span>
                  <span *ngIf="!row.selections.authorizedLicenseUploading">Add</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" class="d-none"
                    (change)="onRowLicenseSelected($event, row)"
                    [disabled]="row.selections.authorizedLicenseUploading" />
                </label>
              </ng-template>
            </td>
            <td class="sabu-text-cell" [title]="row.display.BrandName || ''">{{ row.display.BrandName || '—' }}</td>
            <td class="sabu-text-cell" [title]="row.display.ProductName || ''">{{ row.display.ProductName || '—' }}</td>
            <td class="sabu-select-cell">
              <ng-select class="sabu-ng-select" [items]="statusOptions" bindLabel="label" bindValue="value"
                [(ngModel)]="row.selections.status" (ngModelChange)="onRowStatusChange(row)"
                [clearable]="false" appendTo="body"></ng-select>
            </td>
            <td class="sabu-center-cell">
              <span class="sabu-pill" [class.sabu-pill--yes]="row.configured" [class.sabu-pill--no]="!row.configured">
                {{ row.configured ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="sabu-center-cell">
              <span class="sabu-status" [class.sabu-status--valid]="row.valid" [class.sabu-status--error]="!row.valid">
                {{ row.valid ? 'Valid' : 'Error' }}
              </span>
            </td>
            <td class="sabu-error-cell" *ngIf="errorCount > 0">
              <ng-container *ngIf="row.error; else noErr">
                <span class="sabu-error-chip" [title]="formatErrorTooltip(row.error)">
                  {{ getErrorSummary(row.error) }}
                </span>
              </ng-container>
              <ng-template #noErr><span class="sabu-muted">—</span></ng-template>
            </td>
            <td class="sabu-action-cell">
              <button type="button" class="sabu-config-btn" title="Configure dropdowns" (click)="openRowEditor(row)">
                <i data-lucide="settings"></i>
              </button>
              <button type="button" class="sabu-delete-btn" title="Delete row" (click)="deleteRow(i)">
                <i data-lucide="trash-2"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="sabu-modal__footer">
    <button type="button" class="btn btn--outline" (click)="activeModal.dismiss('Cancel click')">Cancel</button>
    <button type="button" class="btn btn--outline" *ngIf="step === 'preview'" (click)="backToUpload()">Back</button>
    <button type="button" class="btn btn--primary" *ngIf="step === 'preview'"
      [disabled]="validCount === 0 || enrichingIngredients" (click)="confirmImport()">
      <i data-lucide="check"></i>
      Confirm Import ({{ validCount }})
    </button>
  </div>
</div>

<ng-template #assignModalTpl let-modal>
  <div class="sabu-assign">
    <div class="sabu-assign__header">
      <div class="sabu-assign__header-left">
        <div class="sabu-assign__icon">
          <i data-lucide="user-plus"></i>
        </div>
        <div>
          <h5 class="sabu-assign__title">
            {{ assignMode === 'checker-only'
              ? (assignDialogIsBulk ? 'Bulk Assign Checker' : 'Assign Checker')
              : (assignDialogIsBulk ? 'Bulk Assign Products' : 'Assign Maker & Checker') }}
          </h5>
          <p class="sabu-assign__subtitle">
            {{ assignMode === 'checker-only'
              ? 'Select a Checker for Super Admin assignment.'
              : 'Maker and Checker are assigned together.' }}
          </p>
        </div>
      </div>
      <button type="button" class="sabu-assign__close" (click)="closeAssignDialog(modal)">&times;</button>
    </div>

    <div class="sabu-assign__body">
      <div *ngIf="!assignDialogIsBulk && assignTargetProduct" class="sabu-assign__recipe">
        <div>
          <div class="sabu-assign__recipe-name">{{ assignTargetProduct.display.ProductName || 'Untitled' }}</div>
          <div class="sabu-assign__recipe-meta">Row #{{ assignTargetProduct.rowNumber }} · {{ assignTargetProduct.display.BrandName }}</div>
        </div>
      </div>

      <div *ngIf="assignDialogIsBulk" class="sabu-assign__bulk-banner">
        <div class="sabu-assign__bulk-label">Bulk Assignment</div>
        <div class="sabu-assign__bulk-count">{{ getAssignTargetCount() }} products selected</div>
      </div>

      <div class="sabu-assign__field" *ngIf="assignMode === 'maker-and-checker'">
        <label>Select Maker <span class="text-danger">*</span></label>
        <ng-select
          [items]="makerList"
          bindValue="email"
          [(ngModel)]="selectedMakerEmail"
          placeholder="Select maker"
          [searchable]="true"
          [clearable]="true"
          [loading]="isMakerLoading"
          appendTo="body"
        >
          <ng-template ng-label-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
        </ng-select>
      </div>

      <div class="sabu-assign__field">
        <label>Select Checker <span class="text-danger">*</span></label>
        <ng-select
          [items]="checkerList"
          bindValue="email"
          [(ngModel)]="selectedCheckerEmail"
          placeholder="Select checker"
          [searchable]="true"
          [clearable]="true"
          [loading]="isCheckerLoading"
          appendTo="body"
        >
          <ng-template ng-label-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
        </ng-select>
      </div>

      <div class="sabu-assign__field">
        <label>Due date</label>
        <input type="date" class="form-control" [(ngModel)]="selectedDueDate" />
      </div>
      <div class="sabu-assign__field">
        <label>Priority</label>
        <ng-select [items]="assignmentPriorities" [(ngModel)]="selectedPriority" [clearable]="false" appendTo="body"></ng-select>
      </div>
      <div class="sabu-assign__field">
        <label>Notes</label>
        <textarea class="form-control" rows="2" [(ngModel)]="selectedNotes"></textarea>
      </div>
    </div>

    <div class="sabu-assign__footer">
      <button type="button" class="btn btn--outline" (click)="closeAssignDialog(modal)">Cancel</button>
      <button type="button" class="btn btn--primary" (click)="confirmAssignment(modal)">Confirm</button>
    </div>
  </div>
</ng-template>
